import { useAtomValue } from 'jotai';
import { useMemo, useState, useEffect, useCallback } from 'react';
import { useQuery } from '@apollo/client';
import {
  latestSnapshotIdAtom,
  triggerHardRefetchAtom,
  portfolioIdAtom,
} from '~/atoms/dashboardAtom';
import { endpointPortfolioPerformanceGET } from '~/api/def/endpoints/portfolioAnalysisEndpoint';
import {
  HistoricalReturnsWidget,
  type HistoricalReturnsData,
} from './HistoricalReturnsWidget';
import { GET_PORTFOLIO_NAME } from '~/graphql/queries/portfolio';
import { GET_INSTRUMENT_BY_ISIN } from '~/graphql/queries/instruments';
import { useHasBenchmark } from '~/hooks/useHasBenchmark';

export const HistoricalReturnsWidgetWrapper: React.FC<{
  handleRemoveWidget?: (widgetId: string) => void;
  widgetId: string;
  title?: string;
}> = ({ handleRemoveWidget, widgetId, title }) => {
  const latestSnapshotId = useAtomValue(latestSnapshotIdAtom);
  const triggerHardRefetch = useAtomValue(triggerHardRefetchAtom);
  const portfolioId = useAtomValue(portfolioIdAtom);
  const [performanceData, setPerformanceData] =
    useState<HistoricalReturnsData | null>(null);
  const [performanceLoading, setPerformanceLoading] = useState(false);
  const [performanceError, setPerformanceError] = useState<Error | null>(null);
  const [benchmarkName, setBenchmarkName] = useState<string | null>(null);

  // Check if portfolio has a benchmark using the dedicated endpoint
  const { hasBenchmark, isLoading: hasBenchmarkLoading } =
    useHasBenchmark(portfolioId);

  // Fetch portfolio data to get benchmark_index for instrument name lookup
  const { data: portfolioData } = useQuery(GET_PORTFOLIO_NAME, {
    variables: { portfolioId },
    skip: !portfolioId,
  });

  // Get benchmark_index and portfolio name from portfolio
  const benchmarkIndex = useMemo(() => {
    return portfolioData?.EQPortfolio?.[0]?.benchmark_index;
  }, [portfolioData]);

  const portfolioName = useMemo(() => {
    return portfolioData?.EQPortfolio?.[0]?.name;
  }, [portfolioData]);

  // Fetch benchmark instrument by ISIN (only if portfolio has a benchmark)
  const { data: benchmarkInstrument, loading: benchmarkLoading } = useQuery(
    GET_INSTRUMENT_BY_ISIN,
    {
      variables: { isin: benchmarkIndex },
      skip: !hasBenchmark || !benchmarkIndex,
    },
  );

  // Update benchmark name when instrument data changes
  useEffect(() => {
    if (benchmarkInstrument?.EQInstrument?.[0]?.name) {
      setBenchmarkName(benchmarkInstrument.EQInstrument[0].name);
    } else {
      setBenchmarkName(null);
    }
  }, [benchmarkInstrument]);

  // Fetch performance data using REST API
  const fetchPerformanceData = useCallback(async () => {
    if (!latestSnapshotId) return null;

    try {
      setPerformanceLoading(true);
      setPerformanceError(null);

      // Calculate YTD window - if within first 3 months of year, include previous year
      const endDate = new Date();
      const startDate = new Date();
      const currentMonth = endDate.getMonth(); // 0-indexed (0 = Jan, 1 = Feb, 2 = Mar)
      const startYear =
        currentMonth < 3 ? endDate.getFullYear() - 1 : endDate.getFullYear();
      startDate.setFullYear(startYear, 0, 1); // January 1st of the calculated year

      const response = await endpointPortfolioPerformanceGET({
        snapshot_id: latestSnapshotId,
        start_date: startDate.toISOString().split('T')[0], // YYYY-MM-DD format
        end_date: endDate.toISOString().split('T')[0], // YYYY-MM-DD format
        include_current: true,
        include_benchmark: hasBenchmark,
      });
      setPerformanceData(response as HistoricalReturnsData);
      return response;
    } catch (error) {
      console.error('Failed to fetch performance data:', error);

      // Check if it's a 400 error (no data found) - set data to empty instead of error
      if (
        error &&
        typeof error === 'object' &&
        'statusCode' in error &&
        error.statusCode === 400
      ) {
        setPerformanceData(null);
        setPerformanceError(null);
      } else {
        setPerformanceError(error as Error);
      }
      return null;
    } finally {
      setPerformanceLoading(false);
    }
  }, [latestSnapshotId, hasBenchmark]);

  // Fetch performance data only when all prerequisites are ready
  useEffect(() => {
    // Don't fetch if no snapshot
    if (!latestSnapshotId) return;

    // Wait for benchmark check to complete
    if (hasBenchmarkLoading) {
      return;
    }

    // If we have a benchmark, wait for the instrument name query to complete
    if (hasBenchmark && benchmarkLoading) {
      return;
    }

    // All prerequisites ready, fetch the data
    fetchPerformanceData();
  }, [
    latestSnapshotId,
    hasBenchmark,
    hasBenchmarkLoading,
    benchmarkLoading,
    fetchPerformanceData,
  ]);

  // Force hard refetch when triggered by other widgets
  useEffect(() => {
    if (triggerHardRefetch > 0) {
      fetchPerformanceData();
    }
  }, [triggerHardRefetch, fetchPerformanceData]);

  const refetch = useMemo(() => {
    return () => {
      fetchPerformanceData();
    };
  }, [fetchPerformanceData]);

  return (
    <HistoricalReturnsWidget
      data={performanceData}
      isLoading={
        performanceLoading ||
        hasBenchmarkLoading ||
        (hasBenchmark && benchmarkLoading)
      }
      error={performanceError}
      refetch={refetch}
      handleRemoveWidget={handleRemoveWidget}
      widgetId={widgetId}
      title={title}
      snapshotId={latestSnapshotId}
      benchmarkName={benchmarkName}
      portfolioId={portfolioId}
      portfolioName={portfolioName}
    />
  );
};
